import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAxios } from "@/contexts/AxiosContext";
import {
  useBreakdownHistoryIds,
  useBreakdownHistorySnapshots,
  useLibraryItemsSortedByCreatedDate,
  useProjectImages,
  useProjectVideos
} from "@/api/api";
import { ProjectPanelRoute, type LibraryMediaHighlight } from "@/types/routes";
import { formatSceneLabel, formatShotLabel, posterImageIdForCompletedVideoVisual } from "@/services/storyboard";
import type { ProjectDetailsProject, ProjectDetailsStoryboardShot } from "@/types/project";
import { prefetchLibraryItemsMedia } from "./libraryMediaPrefetch";
import {
  activeShotScopeNounFromPanel,
  buildApprovedImageIdsFromJsonItems,
  buildApprovedVideoIdsFromProject,
  buildCurrentlyReferencedMediaIds,
  buildFavoriteImageIdsFromProject,
  buildFavoriteVideoIdsFromProject,
  buildLibraryItems,
  buildStoryboardVideoLibraryItems,
  buildVideoLibraryItemsFromProjectMedia,
  buildVideoReferenceImageIdMap,
  buildViewAllGridEntriesWithHistory,
  buildViewAllLibraryItemsFromProjectMedia,
  filterItemsByActiveShot,
  isCompletedVisualWithPlaybackRefs,
  isFrameVideoPanelSwap,
  libraryItemMatchesHighlight,
  restrictItemsToPanelSelectedEntity,
  type LibraryGridEntry,
  type LibraryItem,
  type SectionFilter
} from "./libraryItems";

const VIEW_ALL_INITIAL_METADATA_BATCH = 40;
const VIEW_ALL_METADATA_BATCH_STEP = 40;

interface UseLibraryDataParams {
  readonly project: ProjectDetailsProject | null;
  readonly projectId: string | null;
  readonly panel: string | undefined;
  readonly activeShotIndex: number;
  readonly charactersList: NonNullable<ProjectDetailsProject["characters"]>;
  readonly safeCharacterIdx: number;
  readonly locationsList: NonNullable<ProjectDetailsProject["locations"]>;
  readonly safeLocationIdx: number;
  readonly libraryHighlight: LibraryMediaHighlight | null;
  readonly open: boolean;
  readonly sidebarTabIsLibrary: boolean;
  readonly onLibraryHighlightMatched?: () => void;
  readonly onLibraryHighlightConsumed?: () => void;
}

/** Filtering/search/highlight state and derived data for RightSidebar's Library tab. */
export function useLibraryData({
  project,
  projectId,
  panel,
  activeShotIndex,
  charactersList,
  safeCharacterIdx,
  locationsList,
  safeLocationIdx,
  libraryHighlight,
  open,
  sidebarTabIsLibrary,
  onLibraryHighlightMatched,
  onLibraryHighlightConsumed
}: UseLibraryDataParams) {
  const [sectionFilter, setSectionFilter] = useState<SectionFilter>("activeShot");
  const [searchQuery, setSearchQuery] = useState("");
  const [approvedForProjectOnly, setApprovedForProjectOnly] = useState(false);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const highlightCardRef = useRef<HTMLDivElement | null>(null);
  const consumedLibraryHighlightKeyRef = useRef<string | null>(null);
  const previousPanelRef = useRef<string | undefined>(panel);
  const queryClient = useQueryClient();
  const axiosClient = useAxios();

  useEffect(() => {
    const previousPanel = previousPanelRef.current;
    previousPanelRef.current = panel;

    if (isFrameVideoPanelSwap(previousPanel, panel)) {
      return;
    }

    setSectionFilter("activeShot");
  }, [panel]);

  useEffect(() => {
    setSearchQuery("");
  }, [sectionFilter]);

  const [viewAllLoadedCount, setViewAllLoadedCount] = useState(VIEW_ALL_INITIAL_METADATA_BATCH);

  useEffect(() => {
    setViewAllLoadedCount(VIEW_ALL_INITIAL_METADATA_BATCH);
  }, [projectId]);

  const favoriteImageIds = useMemo(() => buildFavoriteImageIdsFromProject(project), [project]);
  const favoriteVideoIds = useMemo(() => buildFavoriteVideoIdsFromProject(project), [project]);
  const allImageItems = useMemo(() => buildLibraryItems(project, favoriteImageIds), [project, favoriteImageIds]);
  const allVideoVisualItems = useMemo(() => buildStoryboardVideoLibraryItems(project, favoriteVideoIds), [project, favoriteVideoIds]);
  const approvedImageIdsFromJson = useMemo(() => buildApprovedImageIdsFromJsonItems(allImageItems), [allImageItems]);
  const approvedVideoIdsFromJson = useMemo(() => buildApprovedVideoIdsFromProject(project), [project]);
  const videoReferenceImageIdFallback = useMemo(() => buildVideoReferenceImageIdMap(project), [project]);

  const viewAllMediaEnabled = sectionFilter === "viewAll" && Boolean(projectId);
  const bucketImagesEnabled = (sectionFilter === "viewAll" || sectionFilter === "shots") && Boolean(projectId);
  const bucketVideosEnabled = (sectionFilter === "viewAll" || sectionFilter === "shots" || sectionFilter === "videos") && Boolean(projectId);
  const skipMetadataFetchForViewAll = sectionFilter === "viewAll";
  const {
    images: projectBucketImages,
    isLoading: projectImagesLoading,
    isError: projectImagesError
  } = useProjectImages(projectId, { enabled: bucketImagesEnabled, skipMetadataFetch: skipMetadataFetchForViewAll });
  const {
    videos: projectBucketVideos,
    isLoading: projectVideosLoading,
    isError: projectVideosError
  } = useProjectVideos(projectId, { enabled: bucketVideosEnabled, skipMetadataFetch: skipMetadataFetchForViewAll });

  const handleLoadMoreViewAll = useCallback(() => {
    setViewAllLoadedCount((prev) => prev + VIEW_ALL_METADATA_BATCH_STEP);
  }, []);

  const { data: breakdownHistoryIds = [], isLoading: breakdownHistoryIdsLoading } = useBreakdownHistoryIds(projectId, {
    enabled: viewAllMediaEnabled
  });
  const { snapshots: breakdownHistorySnapshots, isLoading: breakdownHistorySnapshotsLoading } = useBreakdownHistorySnapshots(
    projectId,
    breakdownHistoryIds,
    viewAllMediaEnabled
  );

  const bucketLibraryItems = useMemo(
    () =>
      buildViewAllLibraryItemsFromProjectMedia(projectBucketImages, projectBucketVideos, approvedImageIdsFromJson, approvedVideoIdsFromJson, {
        fallbackImageIdByVideoId: videoReferenceImageIdFallback,
        favoriteImageIds,
        favoriteVideoIds
      }),
    [
      projectBucketImages,
      projectBucketVideos,
      approvedImageIdsFromJson,
      approvedVideoIdsFromJson,
      videoReferenceImageIdFallback,
      favoriteImageIds,
      favoriteVideoIds
    ]
  );
  const characterAndLocationImageIds = useMemo(
    () => new Set(allImageItems.filter((item) => item.section === "characters" || item.section === "locations").map((item) => item.imageId)),
    [allImageItems]
  );
  const bucketImageOnlyItems = useMemo(
    () => bucketLibraryItems.filter((item) => item.libraryVideoId === undefined && !characterAndLocationImageIds.has(item.imageId)),
    [bucketLibraryItems, characterAndLocationImageIds]
  );
  const bucketVideoOnlyItems = useMemo(
    () => buildVideoLibraryItemsFromProjectMedia(projectBucketVideos, approvedVideoIdsFromJson, videoReferenceImageIdFallback, favoriteVideoIds),
    [projectBucketVideos, approvedVideoIdsFromJson, videoReferenceImageIdFallback, favoriteVideoIds]
  );

  const currentReferencedMediaIds = useMemo(
    () => buildCurrentlyReferencedMediaIds(allImageItems, allVideoVisualItems),
    [allImageItems, allVideoVisualItems]
  );

  const selectedCharacterId = charactersList[safeCharacterIdx]?.id ?? null;
  const selectedLocationId = locationsList[safeLocationIdx]?.id ?? null;

  /** Map reference_image_id -> video_id for completed visuals (image rows without `libraryVideoId`). */
  const completedVideoByImageId = useMemo(() => {
    const map = new Map<string, string>();
    const shots = project?.["storyboard-shots"] ?? [];

    for (const shot of shots) {
      for (const v of shot.visuals ?? []) {
        const posterImageId = posterImageIdForCompletedVideoVisual(shot, v);
        if (!isCompletedVisualWithPlaybackRefs(v, posterImageId)) {
          continue;
        }

        map.set(posterImageId, v["video_id"] as string);
      }
    }

    return map;
  }, [project]);

  /** Items visible for the current filter chips (and approved/favorites toggles), before search text — drives grid + autocomplete options. */
  const scopedLibraryItems = useMemo(() => {
    let list: LibraryItem[];

    switch (sectionFilter) {
      case "activeShot":
        list = filterItemsByActiveShot(allImageItems, allVideoVisualItems, {
          project,
          activeShotIndex,
          panel,
          selectedCharacterId,
          selectedLocationId
        });
        break;
      case "characters":
        list = allImageItems.filter((i) => i.section === "characters");
        break;
      case "locations":
        list = allImageItems.filter((i) => i.section === "locations");
        break;
      case "shots":
        list = bucketImageOnlyItems;
        break;

      case "videos":
        list = bucketVideoOnlyItems;
        break;
      case "viewAll":
        list = bucketLibraryItems;
        break;

      default:
        list = allImageItems;
        break;
    }

    list = restrictItemsToPanelSelectedEntity(list, panel, sectionFilter, selectedCharacterId, selectedLocationId);

    if (approvedForProjectOnly) {
      list = list.filter((i) => i.isApproved);
    }

    if (favoritesOnly) {
      list = list.filter((i) => i.isFavorite);
    }

    return list;
  }, [
    allImageItems,
    allVideoVisualItems,
    bucketLibraryItems,
    bucketImageOnlyItems,
    bucketVideoOnlyItems,
    project,
    activeShotIndex,
    panel,
    sectionFilter,
    approvedForProjectOnly,
    favoritesOnly,
    selectedCharacterId,
    selectedLocationId
  ]);

  const viewAllMediaLoading =
    bucketVideosEnabled &&
    (projectImagesLoading ||
      projectVideosLoading ||
      (sectionFilter === "viewAll" && (breakdownHistoryIdsLoading || breakdownHistorySnapshotsLoading)));
  const viewAllMediaError = bucketVideosEnabled && (projectImagesError || projectVideosError);
  const viewAllHasMore = sectionFilter === "viewAll" && viewAllLoadedCount < scopedLibraryItems.length;

  const librarySearchSuggestions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];

    for (const item of scopedLibraryItems) {
      const n = item.displayName?.trim();
      if (n !== undefined && n !== "" && !seen.has(n)) {
        seen.add(n);
        out.push(n);
      }
    }

    return out.toSorted((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [scopedLibraryItems]);

  const activeShotBackTooltip = `Back to active ${activeShotScopeNounFromPanel(panel)}`;

  const contextBarDescription = useMemo(() => {
    if (panel === ProjectPanelRoute.Characters) {
      const name = charactersList[safeCharacterIdx]?.name?.trim();
      return name !== undefined && name !== null && name !== "" ? name : "—";
    }

    if (panel === ProjectPanelRoute.Locations) {
      const name = locationsList[safeLocationIdx]?.name?.trim();
      return name !== undefined && name !== null && name !== "" ? name : "—";
    }

    const shots = project?.["storyboard-shots"] ?? [];
    const activeShot = shots[activeShotIndex] as ProjectDetailsStoryboardShot | undefined;
    const sceneLabel = formatSceneLabel(activeShot?.["scene-id"], activeShotIndex);
    const shotLabel = formatShotLabel(activeShot?.["shot-id"], activeShotIndex);
    const frameContext =
      activeShot?.["ref-image-prompt"]?.trim() || activeShot?.["shot-description"]?.trim() || activeShot?.["shot-name"]?.trim() || "";

    return [sceneLabel, shotLabel, frameContext].filter(Boolean).join(" ") || "—";
  }, [panel, project, activeShotIndex, charactersList, safeCharacterIdx, locationsList, safeLocationIdx]);

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (q.length === 0) {
      return scopedLibraryItems;
    }

    return scopedLibraryItems.filter((i) => (i.displayName ?? "").toLowerCase().includes(q));
  }, [scopedLibraryItems, searchQuery]);

  const itemsToSortByCreatedDate = useMemo(() => (sectionFilter === "viewAll" ? [] : filteredItems), [sectionFilter, filteredItems]);
  const { sortedItems } = useLibraryItemsSortedByCreatedDate(projectId, itemsToSortByCreatedDate);
  const displayItems = sectionFilter === "viewAll" ? filteredItems : sortedItems;

  const displayEntries = useMemo<LibraryGridEntry[]>(() => {
    if (sectionFilter !== "viewAll" || breakdownHistorySnapshots.length === 0) {
      return displayItems.map((item) => ({ kind: "item" as const, item }));
    }

    return buildViewAllGridEntriesWithHistory(displayItems, currentReferencedMediaIds, breakdownHistorySnapshots);
  }, [displayItems, sectionFilter, breakdownHistorySnapshots, currentReferencedMediaIds]);

  const itemsToPrefetch = useMemo(
    () => (sectionFilter === "viewAll" ? scopedLibraryItems.slice(0, viewAllLoadedCount) : scopedLibraryItems),
    [sectionFilter, scopedLibraryItems, viewAllLoadedCount]
  );

  useEffect(() => {
    if (!open || !sidebarTabIsLibrary || projectId === null) {
      return;
    }

    if (viewAllMediaLoading) {
      return;
    }

    void prefetchLibraryItemsMedia(queryClient, axiosClient, projectId, itemsToPrefetch, completedVideoByImageId);
  }, [axiosClient, completedVideoByImageId, open, projectId, queryClient, itemsToPrefetch, sidebarTabIsLibrary, viewAllMediaLoading]);

  const highlightedLibraryItemId = useMemo(() => {
    if (!libraryHighlight || !open) {
      return null;
    }

    const match = displayItems.find((item) => libraryItemMatchesHighlight(item, libraryHighlight, completedVideoByImageId));
    return match?.id ?? null;
  }, [completedVideoByImageId, displayItems, libraryHighlight, open]);

  useEffect(() => {
    if (!open || !libraryHighlight) {
      return;
    }

    const highlightKey = `${libraryHighlight.kind}:${libraryHighlight.mediaId}`;
    if (highlightedLibraryItemId === null) {
      return;
    }

    onLibraryHighlightMatched?.();

    const scrollTimer = window.setTimeout(() => {
      highlightCardRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 300);

    const consumeTimer = window.setTimeout(() => {
      if (consumedLibraryHighlightKeyRef.current === highlightKey) {
        return;
      }

      consumedLibraryHighlightKeyRef.current = highlightKey;
      onLibraryHighlightConsumed?.();
    }, 2000);

    return () => {
      window.clearTimeout(scrollTimer);
      window.clearTimeout(consumeTimer);
    };
  }, [highlightedLibraryItemId, libraryHighlight, onLibraryHighlightConsumed, onLibraryHighlightMatched, open]);

  useEffect(() => {
    if (libraryHighlight === null) {
      consumedLibraryHighlightKeyRef.current = null;
    }
  }, [libraryHighlight]);

  useEffect(() => {
    if (!open || !libraryHighlight || highlightedLibraryItemId !== null) {
      return;
    }

    const revealTimer = window.setTimeout(() => {
      setSectionFilter("viewAll");
      setSearchQuery("");
      setApprovedForProjectOnly(false);
      setFavoritesOnly(false);
    }, 200);

    return () => {
      window.clearTimeout(revealTimer);
    };
  }, [highlightedLibraryItemId, libraryHighlight, open]);

  return {
    sectionFilter,
    setSectionFilter,
    searchQuery,
    setSearchQuery,
    approvedForProjectOnly,
    setApprovedForProjectOnly,
    favoritesOnly,
    setFavoritesOnly,
    displayEntries,
    librarySearchSuggestions,
    completedVideoByImageId,
    highlightedLibraryItemId,
    highlightCardRef,
    viewAllMediaLoading,
    viewAllMediaError,
    viewAllHasMore,
    onLoadMoreViewAll: handleLoadMoreViewAll,
    contextBarDescription,
    activeShotBackTooltip
  };
}
